import {
  NoDataError,
  onSubmitForm,
  ValidationError,
} from 'App/data-export/components/DataExport/DataExportModalDrawer/_utils/onSubmitForm/onSubmitForm'
import { useIntervalAndValueTypesOptions } from 'App/data-export/components/DataExport/DataExportModalDrawer/_utils/useIntervalAndValueTypesOptions'
import { DateRangeSelect } from 'App/data-export/components/DataExport/DataExportModalDrawer/components/DateRangeSelect'
import { FieldAssetsSelects } from 'App/data-export/components/DataExport/DataExportModalDrawer/components/FieldAssetsSelects/FieldAssetsSelects'
import { IntervalSelect } from 'App/data-export/components/DataExport/DataExportModalDrawer/components/IntervalSelect'
import { PropertySelect } from 'App/data-export/components/DataExport/DataExportModalDrawer/components/PropertySelect'
import { ValueTypesSelect } from 'App/data-export/components/DataExport/DataExportModalDrawer/components/ValueTypesSelect/ValueTypesSelect'
import { dataExportStore } from 'App/data-export/store/dataExportStore'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { translate } from 'i18n/i18n'
import React, { useCallback, useRef } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { captureFrontendError } from 'utils/captureFrontendError'
import { showNotification } from 'utils/showNotification'

export const DataExportModalDrawer = () => {
  const { formState } = dataExportStore.useSelector((s) => s)
  const propertyRef = useRef<HTMLDivElement>(null)
  const fieldAssetsRef = useRef<HTMLDivElement>(null)
  const intervalRef = useRef<HTMLDivElement>(null)
  const valueTypesRef = useRef<HTMLDivElement>(null)
  const datesRef = useRef<HTMLDivElement>(null)

  const primaryButtonOnPress = useCallback(async () => {
    try {
      await onSubmitForm()
    } catch (error) {
      if (error instanceof ValidationError) {
        const { formErrors } = dataExportStore.getState()

        // The if/else order is important here. We want to scroll to the most relevant error.
        // This also assumes the relevant ref is visible/mounted. (See the JSX below).
        if (formErrors.propertyId) {
          propertyRef.current?.scrollIntoView({ behavior: 'smooth' })
        } else if (formErrors.fieldAssetIds) {
          fieldAssetsRef.current?.scrollIntoView({ behavior: 'smooth' })
        } else if (formErrors.interval) {
          intervalRef.current?.scrollIntoView({ behavior: 'smooth' })
        } else if (formErrors.valueTypes) {
          valueTypesRef.current?.scrollIntoView({ behavior: 'smooth' })
        } else if (formErrors.dateRange) {
          datesRef.current?.scrollIntoView({ behavior: 'smooth' })
        }
      } else if (error instanceof NoDataError) {
        showNotification({ type: 'error', message: translate.phrases.banyanApp('No data found.') })
      } else {
        showNotification({ type: 'error', message: translate.phrases.banyanApp('There was an error') })

        captureFrontendError(error)
      }

      return Promise.reject()
    }
  }, [])

  const { intervalOptions, valueTypesOptions } = useIntervalAndValueTypesOptions(formState)
  const buttonText = translate.phrases.banyanApp('Download CSV File(s)')

  return (
    <ModalDrawer
      title={translate.phrases.banyanApp('Data Export')}
      primaryButtonText={buttonText}
      primaryButtonOnPress={primaryButtonOnPress}
      primaryButtonAsync={true}
      primaryButtonAsyncOptions={{
        loadingContent: buttonText,
        successContent: buttonText,
        errorContent: buttonText,
        minimumVisualLoadingMilliseconds: 0,
      }}
      opened={true}
      onClose={dataExportStore.actions.closeModal}
      secondaryButtonText={undefined}
      secondaryButtonOnPress={undefined}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX - 1}
      trapFocus={false} // trapFocus is false because we don't want to auto open the property select
    >
      <div css={{ padding: 30, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <div ref={propertyRef}>
            <PropertySelect />
          </div>
          {formState.propertyId && (
            <div ref={fieldAssetsRef}>
              <FieldAssetsSelects />
              {formState.blockIds.length > 0 ||
              formState.points.length > 0 ||
              formState.irrigationZoneEmitterIds.length > 0 ? (
                <>
                  <div ref={intervalRef}>
                    <IntervalSelect options={intervalOptions} />
                  </div>
                  {formState.interval && (
                    <>
                      <div ref={valueTypesRef}>
                        <ValueTypesSelect
                          allowSectionToggle={false}
                          allowGroupToggle={false}
                          options={valueTypesOptions}
                        />
                      </div>
                      <div ref={datesRef}>
                        <DateRangeSelect />
                      </div>
                    </>
                  )}
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </ModalDrawer>
  )
}
